import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { gbp } from "../../context/CartContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Download, Search, CalendarClock } from "lucide-react";

const STAT_CARDS = [
  { key: "orders_today", label: "Orders today" },
  { key: "sales_today", label: "Sales today", money: true },
  { key: "delivery_orders", label: "Delivery" },
  { key: "collection_orders", label: "Collection" },
  { key: "avg_order_value", label: "Avg order", money: true },
];

const STATUS_LABEL = {
  new: "New", preparing: "Preparing", ready: "Ready",
  out_for_delivery: "Out for delivery", completed: "Completed",
};

const LEVELS = {
  green: { dot: "bg-emerald-500", text: "text-emerald-700", ring: "border-emerald-200 bg-emerald-50" },
  amber: { dot: "bg-amber-500", text: "text-amber-700", ring: "border-amber-200 bg-amber-50" },
  red: { dot: "bg-red-500", text: "text-red-700", ring: "border-red-200 bg-red-50" },
};

const fmtDue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
};

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [date, setDate] = useState("");
  const [cost, setCost] = useState(null);

  useEffect(() => { api.get("/admin/system-cost").then((r) => setCost(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (type) params.type = type;
    if (date) params.date = date;
    const [o, st] = await Promise.all([
      api.get("/admin/orders", { params }),
      api.get("/admin/stats"),
    ]);
    setOrders(o.data); setStats(st.data);
  }, [q, type, date]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await api.get("/admin/orders-export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "bettle-chisel-orders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const lvl = LEVELS[cost?.level || "green"];
  const dueLabel = cost ? (cost.days_until_due <= 0 ? "due today" : `in ${cost.days_until_due} day${cost.days_until_due === 1 ? "" : "s"}`) : "";

  return (
    <div className="px-4 md:px-6 py-4">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-head text-2xl md:text-3xl font-bold text-brand-dark">Orders & Reporting</h1>
        <Button data-testid="export-csv-btn" onClick={exportCsv} className="rounded-full bg-brand-dark text-white font-semibold"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="bg-white rounded-2xl p-4 border border-brand-dark/5" data-testid={`stat-${c.key}`}>
            <p className="text-xs text-brand-dark/50">{c.label}</p>
            <p className="font-head text-2xl font-bold text-brand-dark mt-1">{c.money ? gbp(stats[c.key]) : (stats[c.key] ?? 0)}</p>
          </div>
        ))}
      </div>

      {/* System cost — subtle; hidden until first paid month is over */}
      {cost && !cost.hidden && (
        <div className={`rounded-2xl border ${lvl.ring} px-4 py-3 mb-6 flex items-center justify-between gap-3`} data-testid="system-cost">
          <div className="flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full ${lvl.dot} shrink-0`} />
            <div>
              <p className="text-sm font-semibold text-brand-dark flex items-center gap-1.5">
                <CalendarClock className="w-4 h-4 text-brand-dark/40" /> System cost
              </p>
              <p className="text-xs text-brand-dark/50">
                {gbp(cost.base)} base + {gbp(cost.per_order_fee)} × {cost.orders} transaction{cost.orders === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-head text-2xl font-bold text-brand-dark leading-none" data-testid="system-cost-total">{gbp(cost.total)}</p>
            <p className={`text-xs font-semibold mt-1 ${lvl.text}`} data-testid="system-cost-due">Due {fmtDue(cost.due_date)} · {dueLabel}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-dark/40" />
          <Input data-testid="search-orders" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, name or phone" className="pl-9 rounded-xl h-10" />
        </div>
        <select data-testid="filter-type" value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-brand-dark/15 h-10 px-3 bg-white text-sm">
          <option value="">All types</option>
          <option value="delivery">Delivery</option>
          <option value="collection">Collection</option>
        </select>
        <Input data-testid="filter-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl h-10 w-40" />
      </div>

      <div className="bg-white rounded-2xl border border-brand-dark/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-cream text-brand-dark/60 text-left">
            <tr>
              <th className="p-3 font-semibold">Order</th>
              <th className="p-3 font-semibold">Customer</th>
              <th className="p-3 font-semibold">Type</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Time</th>
              <th className="p-3 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`history-row-${o.order_number}`} className="border-t">
                <td className="p-3 font-bold text-brand-dark">#{o.order_number}</td>
                <td className="p-3">{o.customer?.first_name} {o.customer?.last_name}<br /><span className="text-xs text-brand-dark/40">{o.customer?.phone}</span></td>
                <td className="p-3"><Badge className={`border-0 text-white ${o.type === "delivery" ? "bg-brand-teal" : "bg-brand-dark"}`}>{o.type}</Badge></td>
                <td className="p-3"><span className="text-xs">{STATUS_LABEL[o.status] || o.status}</span></td>
                <td className="p-3 text-xs text-brand-dark/50">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-right font-semibold">{gbp(o.total)}</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-brand-dark/40">No orders found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
